"use client";

import React from "react";
import { CircleQuestionMark as HelpCircle, ArrowUpRight, Phone, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface FaqItem {
  id: string;
  question: string;
  content: React.ReactNode;
}
const faqList: FaqItem[] = [{
  id: "permits-dekalb",
  question: "How are building permits handled with the City of Brookhaven and DeKalb County?",
  content: <div className="space-y-3 text-muted-foreground leading-relaxed">
        <p>
          Brookhaven operates its own municipal Community Development Department for local zoning, structural plan reviews, and tree protection ordinances, while coordinating with DeKalb County for watershed management and specific sewer connections.
        </p>
        <p>
          We manage the complete administrative and architectural submission process on your behalf. This includes preparing stamped structural drawings, submitting architectural sets, paying municipal plan-check fees, and scheduling every official field inspection through completion.
        </p>
      </div>
}, {
  id: "living-during-construction",
  question: "Can our family remain in the home during construction?",
  content: <div className="space-y-3 text-muted-foreground leading-relaxed">
        <p>
          This depends directly on the scope and location of the renovation within your home:
        </p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>
            <strong className="text-foreground font-medium">Isolated projects (single bathrooms, basements):</strong> Most homeowners comfortably stay on-site. We erect zip-wall barriers, seal return air registers, and establish dedicated exterior ingress.
          </li>
          <li>
            <strong className="text-foreground font-medium">Kitchen overhauls:</strong> Homeowners frequently stay if a temporary prep area with a mini-fridge, microwave, and wash station is set up elsewhere in the house.
          </li>
          <li>
            <strong className="text-foreground font-medium">Whole-home and multi-room renovations:</strong> When primary mechanical, electrical, and plumbing rough-ins are offline across several levels, temporary off-site accommodation is strongly recommended for comfort and safety.
          </li>
        </ul>
      </div>
}, {
  id: "change-orders",
  question: "How are unforeseen structural issues or requested changes managed?",
  content: <div className="space-y-3 text-muted-foreground leading-relaxed">
        <p>
          Older homes throughout the historic Brookhaven corridors occasionally present concealed conditions once walls are opened, such as legacy framing discrepancies, outmoded wiring, or hidden plumbing runs.
        </p>
        <p>
          When an unmapped condition arises, we halt work in that immediate zone, document the condition photographically, and prepare a written Change Order. Every Change Order clearly details:
        </p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>The technical cause and recommended remediation</li>
          <li>Any associated material or labor adjustment</li>
          <li>The exact impact on the overarching sequence</li>
        </ul>
        <p>
          No supplementary work proceeds without your written digital approval.
        </p>
      </div>
}, {
  id: "timelines-general",
  question: "What dictates project durations and scheduling?",
  content: <div className="space-y-3 text-muted-foreground leading-relaxed">
        <p>
          Rather than relying on generic formulas, every schedule is built backwards from material procurement, structural complexity, and municipal review windows.
        </p>
        <p>
          Timelines proceed through four distinct phases: pre-construction design refinement, permit review by Brookhaven officials, systematic interior or exterior construction, and the final punch-list detailing. Long-lead items such as custom cabinetry, structural steel beams, and specialty architectural glazing are ordered well before demo begins to avoid mid-project standstills.
        </p>
      </div>
}, {
  id: "remodel-vs-move",
  question: "How do I evaluate whether to remodel or purchase another property in Brookhaven?",
  content: <div className="space-y-3 text-muted-foreground leading-relaxed">
        <p>
          Relocating within Brookhaven involves substantial transaction costs, including brokerage commissions, transfer taxes, and competitive neighborhood bidding, often for a property that still requires personal updates.
        </p>
        <p>
          Remodeling allows you to retain your existing property lot, school district alignment, mature trees, and neighborhood relationships while tailoring layout, ceiling heights, and luxury fixtures specifically to your daily lifestyle.
        </p>
      </div>
}, {
  id: "estimates-process",
  question: "How does your detailed estimating and discovery process work?",
  content: <div className="space-y-3 text-muted-foreground leading-relaxed">
        <p>
          We do not produce vague, single-page ballpark figures that leave homeowners vulnerable to mid-project surprises. Our discovery process includes:
        </p>
        <ol className="list-decimal pl-5 space-y-1.5">
          <li>An initial on-site architectural evaluation to inspect foundation, framing, and mechanical capacity.</li>
          <li>Preliminary feasibility planning and conceptual design alignment.</li>
          <li>A detailed itemized scope of work itemizing framing, MEP, surface finishes, tile allowances, and project management oversight.</li>
        </ol>
      </div>
}, {
  id: "contractor-selection",
  question: "What should homeowners check when evaluating residential contractors?",
  content: <div className="space-y-3 text-muted-foreground leading-relaxed">
        <p>
          Residential renovations require technical accountability and rigorous site supervision. Key verification checkpoints include:
        </p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Active state contractor registration, verifiable with the issuing authority.</li>
          <li>Current General Liability and Workers Compensation policies with adequate coverage limits.</li>
          <li>Direct, local experience completing similar projects in Brookhaven and neighboring North Atlanta areas.</li>
          <li>Direct, dedicated on-site project management rather than transient, unmonitored crews.</li>
        </ul>
      </div>
}, {
  id: "design-choices",
  question: "Do you assist with floor plan optimization and architectural selections?",
  content: <div className="space-y-3 text-muted-foreground leading-relaxed">
        <p>
          Yes. Our collaborative pre-construction process helps you navigate layout redesigns, structural load-bearing wall removals, ceiling transitions, and spatial flow.
        </p>
        <p>
          We help align architectural elements—such as kitchen island proportions, scullery layouts, and primary ensuite bathroom layouts—so that sightlines and day-to-day circulation remain balanced and intuitive.
        </p>
      </div>
}, {
  id: "material-selection",
  question: "How are materials, cabinetry, tile, and plumbing fixtures sourced?",
  content: <div className="space-y-3 text-muted-foreground leading-relaxed">
        <p>
          We partner with regional architectural showrooms, stone slab yards, and custom cabinet artisans across Metro Atlanta. You receive access to curated selections of natural quartzite, custom inset cabinetry, commercial-grade plumbing fixtures, and wide-plank hardwoods.
        </p>
        <p>
          All technical specifications, rough-in requirements, and manufacturer delivery schedules are coordinated directly by our build team to help ensure proper on-site prep.
        </p>
      </div>
}, {
  id: "budget-planning",
  question: "How do you help homeowners plan realistic renovation investments?",
  content: <div className="space-y-3 text-muted-foreground leading-relaxed">
        <p>
          Honest budgeting starts with transparent categorization. We separate structural framing, MEP infrastructure, and finish allowances so you clearly understand where funds are dedicated.
        </p>
        <p>
          We also recommend maintaining an appropriate contingency reserve for older homes to ensure unexpected discoveries are handled calmly without compromising the quality of your finished selections.
        </p>
      </div>
}, {
  id: "financing-options",
  question: "What financing methods do homeowners typically utilize?",
  content: <div className="space-y-3 text-muted-foreground leading-relaxed">
        <p>
          Homeowners fund major residential renovations through several common avenues depending on equity and financial strategy:
        </p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Home Equity Lines of Credit (HELOC) or fixed-rate second mortgages.</li>
          <li>Construction-to-permanent renovation loans through trusted regional lending institutions.</li>
          <li>Private cash reserves managed through our structured milestone billing draw schedule.</li>
        </ul>
      </div>
}, {
  id: "warranty-guarantees",
  question: "What warranty coverage applies after your project is complete?",
  content: <div className="space-y-3 text-muted-foreground leading-relaxed">
        <p>
          Every renovation is backed by our workmanship warranty covering craftsmanship and assembly.
        </p>
        <p>
          In addition, all major mechanical components, architectural roofing shingles, waterproof shower systems, windows, and luxury appliances carry their manufacturer warranties, which we organize and deliver upon completion in your homeowner closeout package.
        </p>
      </div>
}, {
  id: "communication-project",
  question: "How will I be kept updated on daily and weekly progress?",
  content: <div className="space-y-3 text-muted-foreground leading-relaxed">
        <p>
          Clear communication prevents uncertainty. You are assigned a project coordinator who oversees day-to-day trade execution.
        </p>
        <p>
          We provide scheduled weekly progress summaries, photo logs of concealed infrastructure before dry-walling, and clear look-ahead calendars detailing which specialty trades are scheduled on-site.
        </p>
      </div>
}, {
  id: "inspections-code",
  question: "Who conducts structural and trade inspections throughout the build?",
  content: <div className="space-y-3 text-muted-foreground leading-relaxed">
        <p>
          Inspections are performed by City of Brookhaven municipal inspectors and our structural engineering partners. Key mandatory checkpoints include:
        </p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Footing and foundation depth checks prior to pouring concrete</li>
          <li>Rough electrical, plumbing, HVAC, and structural framing before wall closure</li>
          <li>Insulation and energy envelope compliance</li>
          <li>Final safety and certificate of occupancy sign-offs</li>
        </ul>
      </div>
}];
export default function RemodelingFaq() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section id="remodelingfaq" data-nav="light" ref={sectionRef} className="relative bg-background text-foreground py-20 lg:py-28 border-t border-border overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-opacity duration-700" style={{
      opacity: isVisible ? 1 : 0
    }}>
        {/* Section Header */}
        <div className="max-w-3xl mb-14 lg:mb-16">
          <div className="inline-flex items-center gap-2 text-primary font-medium text-sm mb-3">
            <HelpCircle className="size-4" />
            <span>Frequently Asked Questions</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold tracking-tight text-foreground text-balance">
            Clear answers for your Brookhaven home renovation
          </h1>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Planning a custom kitchen, luxury bath, or whole-home remodel involves key questions regarding permits, timelines, and craftsmanship. Explore our verified guidance below.
          </p>
        </div>

        {/* Two-Column Grid: Visual Card & Accordion */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Column: Visual panel & Direct Support */}
          <div className="lg:col-span-4 space-y-6 lg:sticky lg:top-24">
            <div className="rounded-xl overflow-hidden border border-border bg-card shadow-sm">
              <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/remodeling_faq_kitchen_work.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/remodeling_faq_kitchen_work.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/remodeling_faq_kitchen_work.png-wvc-sizes" alt="Custom residential kitchen remodeling with high-end cabinetry and island" className="w-full h-full object-cover" loading="lazy" decoding="async" />
              </div>
              <div className="p-6 space-y-4">
                <h2 className="font-serif text-xl font-semibold text-card-foreground">
                  Have a specific question about your home?
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Every residential footprint in Brookhaven brings unique architectural character. Speak directly with our team about your upcoming project plans.
                </p>

                <div className="pt-2 flex flex-col gap-2.5">
                  <Button asChild variant="default" className="w-full justify-between">
                    <Link to="/ContactRequestAnEstimatePage">
                      <span>Request an estimate</span>
                      <ArrowUpRight className="size-4" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="w-full justify-between">
                    <Link to="/OurProjectsPage">
                      <span>Explore our projects</span>
                      <ArrowUpRight className="size-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>

            {/* Quick Assurances Box */}
            <div className="rounded-xl border border-border bg-card/60 p-5 space-y-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Our Project Standard
              </div>
              <ul className="space-y-2.5 text-sm text-foreground">
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Coordination for City of Brookhaven permits</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Itemized scopes and clear digital change orders</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>On-site project coordination</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Right Column: Accordion */}
          <div className="lg:col-span-8">
            <div className="rounded-xl border border-border bg-card p-4 sm:p-8 shadow-xs">
              <Accordion type="single" collapsible className="w-full divide-y divide-border">
                {faqList.map((faq, index) => <AccordionItem key={faq.id} value={faq.id} data-index={index} className="border-b-0 py-1">
                    <AccordionTrigger className="text-base sm:text-lg font-serif font-medium text-foreground py-4 hover:text-primary transition-colors text-left gap-4">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="pt-1 pb-5">
                      {faq.content}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>
          </div>
        </div>
      </div>
    </section>;
}